import { DataService } from "@/services/data";
import { useEffect, useMemo, useState } from "react";
import type { FilterOperator, FilterSchema } from "../types";
import { loadFilterSchema, resolvePath } from "../use-filter-schema";

/** How long the editor waits after a keystroke before checking the formula. */
export const CHECK_DELAY = 250;

/** A record a formula reads fields on: the tested record (the last scope) or one around it. */
export type FormulaScope = {
    index: number;
    name: string;
    model: string;
    icon?: string | null;
    color?: string | null;
};

export type FormulaFunction = { name: string; signature: string; help: string; group: string };

export type FormulaStatus = "valid" | "incomplete" | "invalid";

/** What `POST /crm/app/filter-formula/` answers (`FilterFormulaView`). */
export type FormulaCheck = {
    status: FormulaStatus;
    message: string;
    display: string;
    canonical: string;
    result_type: string | null;
    errors: { start: number; end: number; message: string }[];
    scopes: FormulaScope[];
    functions: FormulaFunction[];
    sample: { record: { id: number | string; label: string; object_url?: string | null }; trace: string; value: string; empty: boolean } | null;
};

/** Where a condition sits: the filtered object and the related records blocks around it. */
export type FormulaContext = { rootModel: string; relations: string[] };

export type FormulaRequest = FormulaContext & { field: string; operator: FilterOperator | "" } & ({ text: string } | { formula: string });

export function checkFormula(request: FormulaRequest): Promise<FormulaCheck> {
    const { rootModel, relations, ...rest } = request;
    return DataService.client
        .url("/crm/app/filter-formula/")
        .post({ model: rootModel, relations, ...rest })
        .json<FormulaCheck>();
}

// The functions do not depend on the formula: the first answer serves every editor after it.
let knownFunctions: FormulaFunction[] = [];
export function rememberFunctions(functions: FormulaFunction[] | undefined): void {
    if (functions?.length) knownFunctions = functions;
}
export function formulaFunctions(): FormulaFunction[] {
    return knownFunctions;
}

/**
 * The scopes of a condition, named as the server names them (`formula.scope_name`): after their
 * object, or after the relation that reaches them when an outer scope already has that name.
 */
export async function loadFormulaScopes(context: FormulaContext): Promise<{ scope: FormulaScope; schema: FilterSchema }[]> {
    const root = await loadFilterSchema(context.rootModel);
    const scopes: { scope: FormulaScope; schema: FilterSchema }[] = [{ scope: { index: 0, name: root.name, model: root.model, icon: root.icon, color: root.color }, schema: root }];
    let current = root;
    for (const relation of context.relations) {
        const resolved = await resolvePath(current.model, relation, true);
        if (!resolved?.relation) break;
        const schema = await loadFilterSchema(resolved.relation.model);
        const taken = new Set(scopes.map(item => item.scope.name.toLowerCase()));
        const label = resolved.labels[resolved.labels.length - 1];
        const name = !taken.has(schema.name.toLowerCase()) ? schema.name : !taken.has(label.toLowerCase()) ? label : `${schema.name} ${scopes.length + 1}`;
        scopes.push({ scope: { index: scopes.length, name, model: schema.model, icon: schema.icon, color: schema.color }, schema });
        current = schema;
    }
    return scopes;
}

export function useFormulaScopes(context: FormulaContext, enabled = true) {
    const key = `${context.rootModel}|${context.relations.join("|")}`;
    const [state, setState] = useState<{ key: string; scopes: { scope: FormulaScope; schema: FilterSchema }[] } | null>(null);
    useEffect(() => {
        if (!enabled) return;
        let cancelled = false;
        loadFormulaScopes(context)
            .then(scopes => !cancelled && setState({ key, scopes }))
            .catch(() => !cancelled && setState({ key, scopes: [] }));
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key, enabled]);
    return useMemo(() => (state && state.key === key ? state.scopes : null), [state, key]);
}
